import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Phone, Settings, Users, X, Check } from "lucide-react";
import {
  GROUP_CHATS,
  USERS,
  CURRENT_USER,
  getInitials,
  getAvatarColor,
  getUserById,
} from "@/mock/data";
import { useChatStore } from "@/store/chatStore";
import { joinRoom, leaveRoom, getSocket } from "@/lib/socket";
import { DMInput } from "@/features/dm/DMInput";
import { DMMessageList } from "@/features/dm/DMMessageList";
import { usePresenceStore } from "@/store/presenceStore";
import type { Message } from "@snowcord/shared";
import "./GroupView.css";

function MemberPanel({ groupId }: { groupId: string }) {
  const group = GROUP_CHATS.find((g) => g.id === groupId);
  if (!group) return null;

  const members = USERS.filter((u) => u.id !== CURRENT_USER.id || group.memberCount > 0);
  const displayMembers = USERS.slice(0, group.memberCount);

  return (
    <div className="member-panel">
      <div className="member-panel__header">
        Members — {group.memberCount}
      </div>
      <div className="member-panel__list">
        {displayMembers.map((user) => {
          const presence = usePresenceStore.getState().getPresence(user.id);
          return (
            <div className="member-panel__item" key={user.id}>
              <div className="member-panel__avatar">
                <div
                  className="member-panel__avatar-img"
                  style={{ background: getAvatarColor(user.id) }}
                >
                  {getInitials(user.displayName)}
                </div>
                <div className={`member-panel__presence member-panel__presence--${presence}`} />
              </div>
              <div className="member-panel__info">
                <div className="member-panel__name">{user.displayName}</div>
                <div className="member-panel__role">
                  {user.id === group.createdBy ? "owner" : "member"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GroupSettings({
  groupId,
  onClose,
}: {
  groupId: string;
  onClose: () => void;
}) {
  const group = GROUP_CHATS.find((g) => g.id === groupId);
  const [tab, setTab] = useState<"general" | "members" | "notifications">("general");
  const [name, setName] = useState(group?.name || "");

  if (!group) return null;

  const displayMembers = USERS.slice(0, group.memberCount);

  return (
    <div className="group-settings" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="group-settings__backdrop" onClick={onClose} />
      <div className="group-settings__panel">
        <div className="group-settings__tabs">
          {(["general", "members", "notifications"] as const).map((t) => (
            <button
              key={t}
              className={`group-settings__tab${tab === t ? " group-settings__tab--active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="group-settings__body">
          {tab === "general" && (
            <div className="group-settings__field">
              <label className="group-settings__label">Group Name</label>
              <input
                className="group-settings__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
          {tab === "members" && (
            displayMembers.map((user) => (
              <div className="group-settings__member-row" key={user.id}>
                <div
                  className="group-settings__member-avatar"
                  style={{ background: getAvatarColor(user.id) }}
                >
                  {getInitials(user.displayName)}
                </div>
                <div className="group-settings__member-info">
                  <div className="group-settings__member-name">{user.displayName}</div>
                  <div className="group-settings__member-role">
                    {user.id === group.createdBy ? "Owner" : "Member"}
                  </div>
                </div>
                {user.id !== CURRENT_USER.id && user.id !== group.createdBy && (
                  <span className="group-settings__member-remove">Remove</span>
                )}
              </div>
            ))
          )}
          {tab === "notifications" && (
            <div style={{ color: "var(--muted)", fontSize: 14 }}>
              Notification settings coming soon.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function GroupView() {
  const { groupId } = useParams<{ groupId: string }>();
  const group = GROUP_CHATS.find((g) => g.id === groupId);
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const chatStore = useChatStore();
  const roomId = groupId ? `group:${groupId}` : "";
  const messages = chatStore.messages.get(roomId) || [];
  const typingUsers = chatStore.typingUsers.get(roomId) || [];

  useEffect(() => {
    if (roomId) joinRoom(roomId);
    return () => { if (roomId) leaveRoom(roomId); };
  }, [roomId]);

  const handleSend = useCallback(
    (content: string) => {
      const socket = getSocket();
      if (!socket || !groupId) return;

      const tempId = `temp-${Date.now()}`;
      const optimisticMsg: Message = {
        id: tempId,
        senderId: CURRENT_USER.id,
        content,
        nonce: "",
        createdAt: new Date().toISOString(),
        editedAt: null,
        isPinned: false,
        reactions: {},
        status: "pending",
      };

      chatStore.addOptimistic(roomId, tempId, optimisticMsg);
      socket.emit("group:message", { groupId, content, nonce: crypto.randomUUID() });

      setTimeout(() => {
        chatStore.confirmOptimistic(roomId, tempId, {
          ...optimisticMsg,
          id: crypto.randomUUID(),
          status: "confirmed",
        });
      }, 300);
    },
    [groupId, roomId],
  );

  if (!group) {
    return (
      <div className="group-view">
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)" }}>
          Select a group chat
        </div>
      </div>
    );
  }

  const typingNames = typingUsers
    .filter((id) => id !== CURRENT_USER.id)
    .map((id) => getUserById(id)?.displayName || "Someone");

  return (
    <div className="group-view" style={{ flexDirection: "row" }}>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <div className="group-header">
          <div
            className="group-header__avatar"
            style={{ background: getAvatarColor(group.id) }}
          >
            {getInitials(group.name)}
          </div>
          <span className="group-header__name">{group.name}</span>
          <span className="group-header__count">
            <Users size={14} /> {group.memberCount}
          </span>
          <div className="group-header__divider" />
          <div className="group-header__actions">
            <button className="group-header__btn" aria-label="Call">
              <Phone size={18} />
            </button>
            <button
              className="group-header__btn"
              onClick={() => setShowMembers(!showMembers)}
              aria-label="Members"
            >
              <Users size={18} />
            </button>
            <button
              className="group-header__btn"
              onClick={() => setShowSettings(true)}
              aria-label="Settings"
            >
              <Settings size={18} />
            </button>
          </div>
        </div>

        <DMMessageList
          messages={messages}
          currentUserId={CURRENT_USER.id}
          getUserDisplayName={(id) => getUserById(id)?.displayName || "Unknown"}
          onLoadMore={() => {}}
          hasMore={false}
          loadingMore={false}
          onReply={() => {}}
          onReact={() => {}}
          onPin={() => {}}
          onEdit={() => {}}
          onDelete={() => {}}
          onRetry={() => {}}
        />

        <DMInput
          placeholder={`Message #${group.name}`}
          onSend={handleSend}
          onTypingStart={() => {
            const socket = getSocket();
            if (socket && groupId) socket.emit("group:typing:start", { groupId });
          }}
          onTypingStop={() => {
            const socket = getSocket();
            if (socket && groupId) socket.emit("group:typing:stop", { groupId });
          }}
          onEmojiToggle={() => setShowEmojiPicker(!showEmojiPicker)}
        />

        {typingNames.length > 0 && (
          <div style={{ fontSize: 12, color: "var(--muted)", padding: "0 16px" }}>
            {typingNames.join(", ")} {typingNames.length === 1 ? "is" : "are"} typing...
          </div>
        )}
      </div>

      {showMembers && groupId && (
        <MemberPanel groupId={groupId} />
      )}

      {showSettings && groupId && (
        <GroupSettings groupId={groupId} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
